import { ScrollToTop } from 'app-lib/reactHooks'
import web3ProviderUtils from 'app-lib/web3ProviderUtils'
import { ReactComponent as OokiWorker } from 'bzx-common/src/assets/images/ooki-worker.svg'
import { acceptNoTradeKey } from 'bzx-common/src/config/appConfig'
import { RootStore } from 'bzx-common/src/stores'
import { errors } from 'ethers'
import { observer } from 'mobx-react'
import React, { Component, Suspense } from 'react'
import { Redirect, Route, Router, Switch } from 'react-router-dom'
import AppError from 'shared-components/AppError'
import InfoBlock from 'shared-components/InfoBlock'
import NetworkError from 'shared-components/NetworkError'
import PreloaderContent from 'shared-components/PreloaderContent/PreloaderContent'
import TransactionTracker from 'shared-components/TransactionTracker'
import Web3ReactExporter from 'shared-components/Web3ReactExporter'
import { Loader } from 'ui-framework'
import { AbstractConnector } from '@web3-react/abstract-connector'
import { Web3ReactProvider } from '@web3-react/core'
import fulcrumProvider from '../services/FulcrumProvider'
import Borrow from './Borrow'
import Compensation from './Compensation'
import Dashboard from './Dashboard'
import Debug from './Debug'
import Header from './Header'
import Lend from './Lend'
import MigrateTokens from './MigrateTokens'
import Staking from './Staking'
import Stats from './Stats'
import ToastNotification from './ToastNotification'
import Tools from './Tools'
import UserProfile from './UserProfile'

const Intercom = React.lazy(() => import('react-intercom'))
const Trading = React.lazy(() => import('./Trading'))
const UIFramework = React.lazy(() => import('./UIFramework'))

interface IAppProps {
  rootStore: RootStore
}

class App extends Component<IAppProps> {
  constructor(props: IAppProps) {
    super(props)
  }

  public componentDidMount(): void {
    errors.setLogLevel('error')
  }

  public async onGetLibrary(provider?: any, connector?: AbstractConnector) {
    connector && (await web3ProviderUtils.getLibrary(fulcrumProvider, connector))
  }

  public render() {
    const { web3Connection, uiStore, services, chain } = this.props.rootStore
    const { isMobileMedia } = uiStore.media
    const chainDisabledMsg = chain?.uiDisabled ? (
      <div className="txt-center margin-bottom-2">
        <OokiWorker height="175" />
        <h2 className="title-page txt-center">
          Ooki on {chain.chainDetails.shortName} is disabled
        </h2>
        <p className="txt-read-optimized txt-explanation bg-alternative padded-1 center-block">
          {chain.uiDisabledReason}
        </p>
        <p className="txt-explanation">Check our social channels for more information.</p>
      </div>
    ) : null

    return (
      <>
        <Web3ReactProvider getLibrary={this.onGetLibrary}>
          <Web3ReactExporter web3Connection={this.props.rootStore.web3Connection} />
        </Web3ReactProvider>

        {process.env.NODE_ENV === 'production' && (
          <Suspense fallback={null}>
            <Intercom
              appID="dfk4n5ut"
              alignment="right"
              hide_default_launcher
              custom_launcher_selector="#intercom-launcher"
            />
          </Suspense>
        )}

        <Router history={services.history}>
          <Header />
          <ScrollToTop />
          <div className="pages-wrap default-content-width">
            {this.props.rootStore.userNotAllowedToTrade && (
              <div>
                <InfoBlock className="margin-bottom-1" storageKey={acceptNoTradeKey}>
                  You are in a region where trading is not allowed.
                </InfoBlock>
              </div>
            )}

            {web3Connection.chainId === undefined && (
              <div className="txt-center">
                {web3Connection.activatingProvider && <p>Connecting your wallet</p>}
                <Loader sizeDots="small" quantityDots={3} />
              </div>
            )}

            {web3Connection.unsupportedNetwork && <NetworkError />}

            {chain && (
              <Switch>
                <Route path="/dashboard" render={() => <Dashboard />} />
                <Route path="/borrow" render={() => chainDisabledMsg || <Borrow />} />
                <Route path="/lend" render={() => chainDisabledMsg || <Lend />} />
                <Route exact path="/stake" render={() => chainDisabledMsg || <Staking />} />
                <Route path="/stats" render={() => chainDisabledMsg || <Stats />} />
                <Route path="/user-profile" render={() => <UserProfile />} />
                <Route exact path="/debug" render={() => <Debug />} />
                <Route exact path="/token-migration" render={() => <MigrateTokens />} />
                <Route exact path="/compensation" render={() => <Compensation />} />
                <Route path="/tools" render={() => <Tools />} />
                <Route
                  path="/trade"
                  render={() =>
                    chainDisabledMsg || (
                      <Suspense fallback={<PreloaderContent />}>
                        <Trading isMobileMedia={isMobileMedia} rootStore={this.props.rootStore} />
                      </Suspense>
                    )
                  }
                />

                <Route
                  exact
                  path="/ui-framework"
                  render={() => (
                    <Suspense fallback={<PreloaderContent />}>
                      <UIFramework />
                    </Suspense>
                  )}
                />

                <Route path="*" render={() => <Redirect to="/dashboard/lobby" />} />
              </Switch>
            )}

            <AppError rootStore={this.props.rootStore} />
            <TransactionTracker />
            <ToastNotification />
            <div
              id="intercom-launcher"
              className={isMobileMedia ? 'hidden' : 'intercom-launcher'}
            />
          </div>
        </Router>
      </>
    )
  }
}

export default observer(App)
